import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { sectionReveal, heroReveal, delayedReveal } from "@/lib/motion";
import axios from "axios";
import { LifeBuoy, Mail, ArrowLeft, Clock, ShieldQuestion, Trash2, Search, Send, Loader2, CheckCircle2, LogIn, KeyRound, CreditCard, Paperclip, X, ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { toast } from "sonner";

const CONTACT_EMAIL = "emergo.support@gmail.com";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const faqs = [
  { icon: LogIn, q: "I'm having trouble signing in", a: "Make sure you're using the email and password linked to your account. If you still can't get in, send us a message below and we'll help you regain access." },
  { icon: KeyRound, q: "I forgot my password", a: "Use the password reset option on the sign-in screen. If you don't receive the reset email, check your spam folder or contact us and we'll assist." },
  { icon: Trash2, q: "How do I delete my account or data?", a: "You can delete your account yourself, right inside the app, on your own terms. Once you press Delete Account, all of your data is permanently wiped. If you need a hand, send us a message below." },
  { icon: LifeBuoy, q: "Something isn't working as expected", a: "Tell us what you were doing, what you expected, and what happened instead. A screenshot helps us fix it faster." },
  { icon: CreditCard, q: "I have a question about a payment", a: "Share the date and amount of the payment in your message. We handle all payment questions directly and will look into it for you." },
  { icon: ShieldQuestion, q: "How is my data used and protected?", a: "We collect only what's needed to run the app and never share it with third parties. See our Privacy Policy for the full details." },
  { icon: Clock, q: "How long until I hear back?", a: "We read every message and aim to reply within a reasonable time. Please include enough detail so we can help on the first reply." },
];

const Block = ({ title, children }) => (
  <motion.section
    {...sectionReveal}
    className="space-y-4"
  >
    <h2 className="font-extrabold tracking-tight text-2xl md:text-[1.6rem] leading-tight text-ink">{title}</h2>
    <div className="space-y-3 text-ink-2 leading-relaxed text-base">{children}</div>
  </motion.section>
);

const BYTES_PER_KB = 1024;
const MAX_UPLOAD_BYTES = 5 * BYTES_PER_KB * BYTES_PER_KB;
const ALLOWED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/gif"];

export default function Support() {
  const [form, setForm] = useState({ name: "", email: "", message: "", website: "" });
  const [screenshot, setScreenshot] = useState(null);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [query, setQuery] = useState("");
  const fileInputRef = useRef(null);

  const previewUrl = useMemo(() => (screenshot ? URL.createObjectURL(screenshot) : null), [screenshot]);
  useEffect(() => () => previewUrl && URL.revokeObjectURL(previewUrl), [previewUrl]);

  const filteredFaqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return faqs;
    return faqs.filter((f) => f.q.toLowerCase().includes(q) || f.a.toLowerCase().includes(q));
  }, [query]);

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error("Please choose an image file (PNG, JPG, WEBP, or GIF).");
      return;
    }
    if (file.size > MAX_UPLOAD_BYTES) {
      toast.error("That image is too large. Maximum size is 5 MB.");
      return;
    }
    setScreenshot(file);
  };

  const removeScreenshot = () => {
    setScreenshot(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSending(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("message", form.message);
      data.append("website", form.website);
      if (screenshot) data.append("screenshot", screenshot);
      await axios.post(`${API}/contact`, data);
      setSent(true);
      setForm({ name: "", email: "", message: "", website: "" });
      removeScreenshot();
      toast.success("Message sent! We'll get back to you soon.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again or email us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-ink antialiased" data-testid="support-page">
      <Header />

      <main className="mx-auto max-w-3xl px-6">
        <motion.div
          {...heroReveal}
          className="pt-16 pb-12 border-b border-gray-200"
        >
          <Link to="/" data-testid="back-to-home-link" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-brand transition-colors mb-8">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
            Back to home
          </Link>
          <p className="text-xs uppercase tracking-[0.08em] font-bold text-brand mb-4">Help</p>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-ink" data-testid="page-title">
            Support
          </h1>
          <p className="mt-6 text-lg text-ink-2 max-w-xl leading-relaxed">
            Need a hand with Emergo? Send us a message below and we'll help with your account, your data, and anything that isn't working right.
          </p>
        </motion.div>

        <div className="py-14 space-y-14">
          {/* Contact form */}
          <motion.section
            {...delayedReveal}
            data-testid="contact-form-section"
          >
            <h2 className="font-extrabold tracking-tight text-2xl md:text-[1.6rem] leading-tight text-ink mb-2">Send Us a Message</h2>
            <p className="text-ink-2 leading-relaxed mb-6">Fill in the form and your message will reach our support team directly.</p>

            {sent ? (
              <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center" data-testid="contact-success">
                <span className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-brand-soft text-brand">
                  <CheckCircle2 className="h-6 w-6" strokeWidth={1.75} />
                </span>
                <p className="font-extrabold tracking-tight text-xl text-ink">Thanks for reaching out</p>
                <p className="mt-2 text-ink-2">We've received your message and will get back to you by email soon.</p>
                <Button variant="outline" className="mt-6 rounded-xl" onClick={() => setSent(false)} data-testid="send-another-button">
                  Send another message
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 space-y-5" data-testid="contact-form">
                {/* Honeypot: hidden from users, catches bots */}
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={handleChange}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="absolute left-[-9999px] h-0 w-0 opacity-0"
                  data-testid="contact-website-honeypot"
                />
                <div className="grid gap-5 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="text-ink-2">Your name</Label>
                    <Input id="name" name="name" value={form.name} onChange={handleChange} placeholder="Jane Doe" required data-testid="contact-name-input" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-ink-2">Your email</Label>
                    <Input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="jane@example.com" required data-testid="contact-email-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message" className="text-ink-2">How can we help?</Label>
                  <Textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder="Describe your question or issue..." rows={6} required data-testid="contact-message-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-ink-2">Attach a screenshot <span className="text-gray-500 font-normal">(optional)</span></Label>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleFile}
                    className="hidden"
                    data-testid="contact-screenshot-input"
                  />
                  {screenshot ? (
                    <div className="flex items-center gap-3 rounded-xl border border-gray-200 bg-surface2 p-3" data-testid="screenshot-preview">
                      <img src={previewUrl} alt="Screenshot preview" className="h-12 w-12 rounded-lg object-cover border border-gray-200" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-ink">{screenshot.name}</p>
                        <p className="text-xs text-gray-500">{(screenshot.size / BYTES_PER_KB).toFixed(0)} KB</p>
                      </div>
                      <button type="button" onClick={removeScreenshot} className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-200 hover:text-brand transition-colors" data-testid="screenshot-remove-button" aria-label="Remove screenshot">
                        <X className="h-4 w-4" strokeWidth={2} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-gray-300 bg-surface2 px-4 py-4 text-sm text-gray-500 transition-colors hover:border-brand hover:text-brand"
                      data-testid="screenshot-choose-button"
                    >
                      <Paperclip className="h-4 w-4" strokeWidth={1.75} />
                      Choose an image <span className="text-gray-500">· PNG, JPG, WEBP, GIF up to 5 MB</span>
                    </button>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-1">
                  <Button type="submit" disabled={sending} className="rounded-xl bg-brand hover:bg-brand-dark text-white font-bold px-6" data-testid="contact-submit-button">
                    {sending ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4" /> Send message</>)}
                  </Button>
                  <p className="text-sm text-gray-500">
                    Or email us at{" "}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-ink-2 underline underline-offset-4 decoration-gray-300 hover:decoration-brand transition-colors" data-testid="contact-email-link">{CONTACT_EMAIL}</a>
                  </p>
                </div>
              </form>
            )}
          </motion.section>

          {/* Searchable FAQ */}
          <Block title="Common Questions">
            <div className="relative">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" strokeWidth={1.75} />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search help topics..."
                className="pl-11 h-12 rounded-xl"
                data-testid="faq-search-input"
              />
            </div>
            {filteredFaqs.length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center" data-testid="faq-empty">
                <p className="text-ink-2">No topics match "{query}".</p>
                <p className="mt-1 text-sm text-gray-500">Try different words, or send us a message above and we'll help directly.</p>
              </div>
            ) : (
              <ul className="space-y-4 not-prose" data-testid="faq-list">
                {filteredFaqs.map((f) => (
                  <li key={f.q} className="rounded-xl border border-gray-200 bg-white p-5" data-testid={`faq-${f.q.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}>
                    <div className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-soft text-brand">
                        <f.icon className="h-4 w-4" strokeWidth={1.75} />
                      </span>
                      <div>
                        <p className="font-medium text-ink">{f.q}</p>
                        <p className="mt-1 text-ink-2 text-[15px] leading-relaxed">{f.a}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Block>

          <Block title="Privacy & Your Data">
            <p>
              For questions about how we handle your information, or to request access, correction, or deletion of your
              data, see our{" "}
              <Link to="/privacy" data-testid="support-privacy-link" className="font-medium text-ink underline underline-offset-4 decoration-gray-300 hover:decoration-brand transition-colors">Privacy Policy</Link>{" "}
              or send us a message using the form above.
            </p>
          </Block>
        </div>
      </main>
      <Footer />
    </div>
  );
}
